import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Heading,
  Text,
  Flex,
  Image,
  useDisclosure,
  Skeleton,
  Stack,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Avatar,
  Icon,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon, ViewIcon, EditIcon } from '@chakra-ui/icons';
import { FaUserSecret, FaUsers } from 'react-icons/fa';
import { PasswordChangeModal } from '../components/Modals';
import AuthContext from '../context/AuthContext';
import { characterService } from '../services/api';

function Dashboard() {
  const { token, user } = useContext(AuthContext);
  const [characters, setCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { isOpen: isPasswordModalOpen, onOpen: onOpenPasswordModal, onClose: onClosePasswordModal } = useDisclosure();
  const currentUserId = user?.id;

  // Color settings for card styling
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    // Fetch characters when the component mounts
    fetchCharacters();
  }, [token]);

  const fetchCharacters = async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const data = await characterService.getAllCharacters();
      setCharacters(data);
    } catch (error) {
      // Surface the failure instead of silently rendering an empty grid, so the
      // user can retry and "load failed" is distinguishable from "no characters".
      console.error('Error fetching characters:', error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSkeletons = () => {
    return Array(6).fill(0).map((_, i) => (
      <Card key={i} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden" boxShadow="md">
        <CardBody>
          <Skeleton height="80px" width="80px" borderRadius="full" mx="auto" mb={4} />
          <Skeleton height="20px" width="60%" mx="auto" mb={2} />
          <Stack spacing={2}>
            <Skeleton height="10px" />
            <Skeleton height="10px" />
            <Skeleton height="10px" />
            <Skeleton height="10px" />
          </Stack>
        </CardBody>
        <CardFooter>
          <Skeleton height="30px" width="100%" borderRadius="md" />
        </CardFooter>
      </Card>
    ));
  };

  const getFormattedHeight = (heightCm) => {
    if (!heightCm) return 'Unknown';
    const totalInches = heightCm / 2.54;
    const heightFeet = Math.floor(totalInches / 12);
    const heightInches = Math.round(totalInches % 12);
    return `${heightFeet}'${heightInches}"`;
  };

  const getFormattedWeight = (weightKg) => {
    if (!weightKg) return 'Unknown';
    return `${Math.round(weightKg * 2.20462)} lbs`;
  };

  const formatText = (text) => {
    if (!text) return 'Unknown';
    return text.replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={8} flexWrap="wrap">
        <Heading as="h1" size="xl" color="brand.600">
          Characters
        </Heading>
        
        <Button
          as={RouterLink}
          to="/create-character"
          colorScheme="brand"
          leftIcon={<AddIcon />}
          size="md"
        >
          Create New Character
        </Button>
      </Flex>

      {isLoading ? (
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
          gap={6}
        >
          {renderSkeletons()}
        </Grid>
      ) : loadError ? (
        <Flex direction="column" align="center" justify="center" py={16} gap={4}>
          <Text fontSize="lg" color="gray.600" textAlign="center">
            We couldn't load your characters. This is usually temporary — please try again.
          </Text>
          <Button colorScheme="brand" onClick={fetchCharacters}>
            Retry
          </Button>
        </Flex>
      ) : characters.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py={16} gap={4}>
          <Text fontSize="lg" color="gray.600" textAlign="center">
            No characters yet. Create your first one to get started.
          </Text>
          <Button as={RouterLink} to="/create-character" colorScheme="brand" leftIcon={<AddIcon />}>
            Create New Character
          </Button>
        </Flex>
      ) : (
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
          gap={6}
        >
          {characters
            .sort((a, b) => (a.character_profile?.character_name || '').localeCompare(b.character_profile?.character_name || ''))
            .map(char => {
              const profile = char.character_profile || {};
              const isOwned = char.createdBy === currentUserId || user?.role === 'admin';
              
              return (
                <Card 
                  key={char._id} 
                  bg={cardBg}
                  borderWidth="1px" 
                  borderColor={cardBorder}
                  borderRadius="lg" 
                  overflow="hidden"
                  position="relative"
                  boxShadow="md"
                  transition="transform 0.3s, box-shadow 0.3s"
                  _hover={{
                    transform: "translateY(-5px)",
                    boxShadow: "lg"
                  }}
                >
                  <Box 
                    position="absolute" 
                    top={0} 
                    right={0}
                    bg={char.isPublic ? "green.500" : "gray.500"}
                    color="white"
                    borderBottomLeftRadius="md"
                    px={2}
                    py={1}
                    fontSize="xs"
                    fontWeight="bold"
                    zIndex={1}
                  >
                    <Tooltip label={char.isPublic ? "Public character" : "Private character"} placement="top">
                      <Flex alignItems="center" gap={1}>
                        <Icon as={char.isPublic ? FaUsers : FaUserSecret} />
                        <Text>{char.isPublic ? "Public" : "Private"}</Text>
                      </Flex>
                    </Tooltip>
                  </Box>

                  <CardBody>
                    <Flex direction="column" align="center">
                      {char.imageData ? (
                        <Image
                          src={`/api/characters/${char._id}/image`}
                          alt={profile.character_name}
                          borderRadius="full"
                          boxSize="80px"
                          objectFit="cover"
                          mb={4}
                        />
                      ) : (
                        <Avatar 
                          size="xl" 
                          mb={4} 
                          name={profile.character_name || 'Unknown Character'} 
                          bg="brand.500"
                        />
                      )}
                      
                      <Heading size="md" textAlign="center" mb={2}>
                        {profile.character_name || 'Unnamed Character'}, {profile?.core_identity?.age || 'Unknown'}
                      </Heading>
                      
                      <Stack spacing={1} align="center" mb={2}>
                        <Text fontSize="sm" color="gray.500">
                          {formatText(profile?.core_identity?.race || 'Unknown')}, {formatText(profile?.core_identity?.gender || 'Unknown')}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {getFormattedHeight(profile?.core_identity?.height_cm)} • {getFormattedWeight(profile?.core_identity?.weight_kg)}
                        </Text>

                        <Text fontSize="sm" color="gray.500">
                          Orientation: {formatText(profile?.core_identity?.sexual_details?.orientation || 'Unknown')}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Cock: {profile?.core_identity?.sexual_details?.cock_length_inches?.value || 0}" x {profile?.core_identity?.sexual_details?.cock_circumference_inches?.value || 0}"
                        </Text>
                      </Stack>
                    </Flex>
                  </CardBody>

                  <Divider />
                  
                  <CardFooter pt={3} pb={3}>
                    <Flex width="100%" justify="center" gap={2}>
                      <Button 
                        as={RouterLink} 
                        to={`/view-character/${char._id}`}
                        leftIcon={<ViewIcon />} 
                        colorScheme="brand" 
                        variant="solid"
                        size="sm"
                        flex="1"
                      >
                        View
                      </Button>
                      
                      {isOwned && (
                        <Button 
                          as={RouterLink}
                          to={`/edit-character/${char._id}`}
                          leftIcon={<EditIcon />}
                          colorScheme="brand"
                          variant="outline"
                          size="sm"
                          flex="1"
                        >
                          Edit
                        </Button>
                      )}
                    </Flex>
                  </CardFooter>
                </Card>
              );
            })
          }
        </Grid>
      )}

      <PasswordChangeModal isOpen={isPasswordModalOpen} onClose={onClosePasswordModal} />
    </Box>
  );
}

export default Dashboard;